import { Link, useLocation } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { SignedIn, useAuth } from '@clerk/clerk-react';
import UserMenu from './UserMenu';
import SearchAutocomplete from '../search/SearchAutocomplete';
import { useAccessLevel } from '../../hooks/useAccessLevel';
import { useAuthModal } from '../auth/AuthModalContext';
import MyOrbconLabel from '../brand/MyOrbconLabel';
import { useUserSidebar } from '../../hooks/useUserSidebar';
import { ORBCON_REPORT_NAV_LABEL, ORBCON_REPORT_PATH } from '../../content/orbconReport';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
  const location = useLocation();
  const { canViewElearning, canViewData } = useAccessLevel();
  const { openAuthModal } = useAuthModal();
  const { setOpen: setSidebarOpen } = useUserSidebar();
  const { isLoaded, isSignedIn } = useAuth();
  const showAccountMenu = Boolean(isLoaded && isSignedIn);

  const navLinkClass = (...paths: string[]) => {
    const isActive = paths.some((path) =>
      path === '/'
        ? location.pathname === '/'
        : location.pathname === path || location.pathname.startsWith(path + '/')
    );
    return `block text-lg font-medium min-h-[44px] flex items-center ${
      isActive ? 'text-primary' : 'text-gray-700 hover:text-primary'
    }`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="fixed inset-0 bg-black bg-opacity-50"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="fixed right-0 top-0 h-full w-80 bg-white shadow-xl">
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <img src="/images/ORBCoN-EN-Colour.png" alt="ORBCoN" className="h-8" />
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 focus-ring rounded-md"
              aria-label="Close menu"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="p-4 border-b border-gray-200">
            <SearchAutocomplete onClose={onClose} />
          </div>

          <nav className="flex-1 overflow-y-auto p-4">
            <ul className="space-y-4">
              <li>
                <Link to="/" onClick={onClose} className={navLinkClass('/')}>
                  Home
                </Link>
              </li>
              <li>
                <Link to="/resources" onClick={onClose} className={navLinkClass('/resources', '/toolkits', '/references')}>
                  Resources
                </Link>
              </li>
              <li>
                <Link to={ORBCON_REPORT_PATH} onClick={onClose} className={navLinkClass(ORBCON_REPORT_PATH)}>
                  {ORBCON_REPORT_NAV_LABEL}
                </Link>
              </li>
              <li>
                <Link to="/events" onClick={onClose} className={navLinkClass('/events')}>
                  Events
                </Link>
              </li>
              {canViewElearning && (
                <li>
                  <Link to="/elearning" onClick={onClose} className={navLinkClass('/elearning')}>
                    eLearning
                  </Link>
                </li>
              )}
              {canViewData && (
                <li>
                  <Link to="/data" onClick={onClose} className={navLinkClass('/data')}>
                    Data
                  </Link>
                </li>
              )}
              <SignedIn>
                <li>
                  <button
                    type="button"
                    className="block text-lg font-medium min-h-[44px] flex items-center text-gray-700 hover:text-primary"
                    onClick={() => {
                      onClose();
                      setSidebarOpen(true);
                    }}
                  >
                    <MyOrbconLabel iconClassName="h-6 w-6" />
                  </button>
                </li>
              </SignedIn>
            </ul>
          </nav>

          <div className="p-4 border-t border-gray-200 space-y-2">
            {showAccountMenu ? (
              <UserMenu variant="stack" onNavigate={onClose} />
            ) : (
              <>
                <button
                  type="button"
                  className="w-full min-h-[44px] py-2 px-4 bg-primary text-white rounded-md font-medium hover:bg-primary-dark focus-ring"
                  onClick={() => {
                    onClose();
                    openAuthModal('sign-in');
                  }}
                >
                  Sign In
                </button>
                <button
                  type="button"
                  className="w-full min-h-[44px] py-2 px-4 border border-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-50 focus-ring"
                  onClick={() => {
                    onClose();
                    openAuthModal('sign-up');
                  }}
                >
                  Sign Up
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
